import dayjs, { Dayjs } from 'dayjs'
import type { CalendarEvent } from './mock-data'

// Geometry and grouping for the dashboard calendar's Day / Week / Month modes.
//
// Follows the player's mobile schedule, which solves the same problem: a
// minute-positioned timeline, a Monday-first week, a six-row month grid, and
// concurrent events sharing a row as side-by-side lanes so each keeps the vertical
// extent of its own time span.
//
// The one thing added here is a cap. The mobile version slices the row into as many
// lanes as it needs; on this panel a third lane leaves nothing readable, so lanes
// stop at two and whatever will not fit is reported as a count on the last one.

export type CalendarViewMode = 'day' | 'week' | 'month'

/** Pixel height of one hour row; every vertical measurement derives from this. */
export const HOUR_HEIGHT = 80
/** Below this a card cannot fit its title and time, so none is drawn shorter. */
const MIN_BLOCK_HEIGHT = 56
/** Gap under each block so two consecutive events do not read as one. */
const BLOCK_GAP = 4
/**
 * Lanes a row is split into, at most.
 *
 * Two side-by-side cards still show their titles; a third column would leave each
 * too narrow to read. Anything that will not fit is reported as a count on the last
 * lane, which is the only way the viewer learns it exists at all.
 */
export const MAX_LANES = 2

const MINUTES_PER_DAY = 24 * 60
/** A card only ever renders this short when its event genuinely is. */
const MIN_RENDER_MINUTES = (MIN_BLOCK_HEIGHT / HOUR_HEIGHT) * 60
const FALLBACK_DURATION_MINUTES = 60

export const DATE_KEY = 'YYYY-MM-DD'

/**
 * dayjs' `startOf('week')` is locale-dependent and lands on Sunday. The design is
 * Monday-first everywhere, so shift back by `(day + 6) % 7` rather than pulling in
 * the isoWeek plugin for one calculation.
 */
export const mondayOf = (date: Dayjs): Dayjs => date.subtract((date.day() + 6) % 7, 'day').startOf('day')

/** First cell of a month grid: the Monday on or before the 1st. */
export const monthGridStart = (date: Dayjs): Dayjs => mondayOf(date.startOf('month'))

/**
 * The window a mode actually displays, which is the window worth fetching.
 *
 * Day and Week both render one day's timeline, but Week also shows the Mon–Sun
 * strip with a dot on the days that hold something — so the range is the week in
 * both cases, and switching between them needs no new request.
 */
export const visibleRange = (mode: CalendarViewMode, selectedDate: Date): { from: string; to: string } => {
  const selected = dayjs(selectedDate)
  const start = mode === 'month' ? monthGridStart(selected) : mondayOf(selected)
  // Six rows, always, so the grid's height does not jump between months.
  const days = mode === 'month' ? 42 : 7
  return {
    from: start.startOf('day').toISOString(),
    to: start
      .add(days - 1, 'day')
      .endOf('day')
      .toISOString()
  }
}

/** Minutes from midnight for an `HH:mm` clock string, or null when unparsable. */
const clockMinutes = (value: string): number | null => {
  const [hours, minutes] = value.split(':').map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return hours * 60 + minutes
}

interface EventSpan {
  event: CalendarEvent
  startMinute: number
  endMinute: number
}

/**
 * The minute range an event occupies, widened to the shortest drawable block.
 *
 * Collision detection has to reason about what is *painted*, not what was
 * scheduled: two fifteen-minute events ten minutes apart do not overlap on the
 * clock, but their cards do, and without this the second would be drawn straight
 * over the first.
 */
const toSpan = (event: CalendarEvent): EventSpan | null => {
  const startMinute = clockMinutes(event.start)
  if (startMinute === null) return null

  const rawEnd = clockMinutes(event.end)
  // An end before its start means the event runs past midnight; it is clipped to
  // the end of the day rather than drawn with a negative height.
  const duration = rawEnd !== null && rawEnd > startMinute ? rawEnd - startMinute : FALLBACK_DURATION_MINUTES

  return {
    event,
    startMinute,
    endMinute: Math.min(MINUTES_PER_DAY, startMinute + Math.max(MIN_RENDER_MINUTES, duration))
  }
}

/** One card placed on the timeline. */
export interface CalendarBlock {
  event: CalendarEvent
  /** Offset from the top of the timeline, in pixels. */
  top: number
  height: number
  /**
   * Events overlapping this one that no card was drawn for. Shown on the card as
   * "+N", so a hidden event is still announced.
   */
  overflowCount: number
  /** 0-based column among the events it shares the row with. */
  lane: number
  /** Columns the row is split into — 1 when nothing collides. */
  laneCount: number
}

/**
 * Splits one collision into side-by-side lanes.
 *
 * Each event takes the first lane whose previous occupant has already finished, so
 * a row only widens when things genuinely run at the same moment — two sessions
 * back to back inside a longer one share a lane rather than forcing a third
 * column. An event that finds every lane busy is dropped and counted on the card
 * still occupying the last lane, which is the one it would have sat beside.
 */
const assignLanes = (cluster: EventSpan[], minuteToPx: (minute: number) => number): CalendarBlock[] => {
  const laneEnds: number[] = []
  const occupant: (CalendarBlock | undefined)[] = []
  const blocks: CalendarBlock[] = []

  for (const span of cluster) {
    let lane = laneEnds.findIndex(end => end <= span.startMinute)
    if (lane === -1) lane = laneEnds.length

    if (lane >= MAX_LANES) {
      const host = occupant[MAX_LANES - 1]
      if (host) host.overflowCount += 1
      continue
    }

    const top = minuteToPx(span.startMinute)
    const block: CalendarBlock = {
      event: span.event,
      top,
      height: Math.max(MIN_BLOCK_HEIGHT, minuteToPx(span.endMinute) - top - BLOCK_GAP),
      overflowCount: 0,
      lane,
      // Filled in below: the row's width is only known once every event in the
      // collision has been placed.
      laneCount: 1
    }
    laneEnds[lane] = span.endMinute
    occupant[lane] = block
    blocks.push(block)
  }

  for (const block of blocks) block.laneCount = laneEnds.length
  return blocks
}

/**
 * Positions one day's events, splitting the row wherever they collide.
 *
 * Sorted by start so a collision is a contiguous run: any event beginning after
 * everything before it has ended starts a fresh one, which keeps unrelated events
 * at full width instead of inheriting a split.
 */
export const buildCalendarBlocks = (events: CalendarEvent[], hours: number[]): CalendarBlock[] => {
  if (hours.length === 0) return []
  const firstHour = hours[0]
  const minuteToPx = (minute: number) => ((minute - firstHour * 60) / 60) * HOUR_HEIGHT

  const spans = events
    .map(toSpan)
    .filter((span): span is EventSpan => span !== null)
    .sort((a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute)

  const blocks: CalendarBlock[] = []
  let cluster: EventSpan[] = []
  let clusterEnd = -1

  for (const span of spans) {
    if (cluster.length > 0 && span.startMinute >= clusterEnd) {
      blocks.push(...assignLanes(cluster, minuteToPx))
      cluster = []
    }
    cluster.push(span)
    clusterEnd = Math.max(clusterEnd, span.endMinute)
  }
  if (cluster.length > 0) blocks.push(...assignLanes(cluster, minuteToPx))

  return blocks
}

/**
 * Hour rows to draw: a default working window, widened to cover any event that
 * starts earlier or ends later so nothing scheduled is cut off.
 */
export const timelineHours = (events: CalendarEvent[]): number[] => {
  let low = 8
  let high = 20
  for (const event of events) {
    const start = clockMinutes(event.start)
    const end = clockMinutes(event.end)
    if (start !== null) {
      low = Math.min(low, Math.floor(start / 60))
      high = Math.max(high, Math.floor(start / 60))
    }
    if (end !== null) high = Math.max(high, Math.min(23, Math.ceil(end / 60)))
  }
  return Array.from({ length: high - low + 1 }, (_, index) => low + index)
}

/** Groups events by their ISO date, for the week strip's dots and the month grid. */
export const groupByDate = (events: CalendarEvent[]): Map<string, CalendarEvent[]> => {
  const byDate = new Map<string, CalendarEvent[]>()
  for (const event of events) {
    if (!event.date) continue
    byDate.set(event.date, [...(byDate.get(event.date) ?? []), event])
  }
  return byDate
}
